import { clampRangeToBounds, fitFixedDurationInBounds } from '@app/core/range';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

/**
 * Stream screen state interface.
 * Excludes segments (handled by React Query) and isLoaded (handled by React Query loading states).
 */
export interface StreamScreenState {
  readonly userRange: {
    readonly from: number;
    readonly to: number;
    /** True until range is synced from URL or set by user; avoids relying on 0,0 as "not initialized". */
    readonly isLoading: boolean;
  };
  readonly segmentsRange: {
    readonly from: number | null;
    readonly to: number | null;
  };
  readonly cursor: {
    readonly ts: number | null;
  };
  readonly focus: {
    readonly from: number;
    readonly to: number;
  };
}

interface StreamScreenStore extends StreamScreenState {
  // Actions
  setUserRange: (from: number, to: number) => void;
  setSegmentsRange: (from: number | null, to: number | null) => void;
  setCursor: (ts: number) => void;
  clearCursor: () => void;
  setFocus: (focus: { from: number; to: number }) => void;
  setFocusCenteredAtTs: (targetTs: number, duration: number) => void;
  setFocusAtTs: (targetTs: number) => void;
}

const initialState: StreamScreenState = {
  userRange: {
    from: 0,
    to: 0,
    isLoading: true,
  },
  segmentsRange: {
    from: null,
    to: null,
  },
  cursor: {
    ts: null,
  },
  focus: {
    from: 0,
    to: 0,
  },
};

/**
 * Zustand store for managing stream screen UI state.
 * Replaces Redux implementation for main, cursor, and focus state.
 * Segments are handled by React Query.
 */
export const useStreamScreenStore = create<StreamScreenStore>()((set) => ({
  ...initialState,

  setUserRange: (from, to) => {
    set((_state) => ({
      userRange: {
        from,
        to,
        isLoading: false,
      },
      cursor: {
        ts: null, // Clear cursor when range changes
      },
      segmentsRange: {
        from: null, // Clear segments range - they're now stale
        to: null,
      },
    }));
  },

  setSegmentsRange: (from, to) => {
    set({
      segmentsRange: {
        from,
        to,
      },
    });
  },

  setCursor: (ts) => {
    set({
      cursor: {
        ts,
      },
    });
  },

  clearCursor: () => {
    set({
      cursor: {
        ts: null,
      },
    });
  },

  setFocus: (focus) => {
    set((state) => ({
      focus: clampRangeToBounds(focus, state.userRange),
    }));
  },

  setFocusCenteredAtTs: (targetTs, duration) => {
    set((state) => ({
      focus: fitFixedDurationInBounds(targetTs, duration, state.userRange, 'center'),
    }));
  },

  setFocusAtTs: (targetTs) => {
    set((state) => {
      const duration = state.focus.to - state.focus.from;

      return {
        focus: fitFixedDurationInBounds(targetTs, duration, state.userRange, 'start'),
      };
    });
  },
}));

/**
 * Hook to get stream range (from, to).
 * Uses useShallow to perform shallow comparison and avoid infinite render loops.
 */
export function useStreamUserDefinedRange() {
  return useStreamScreenStore(
    useShallow((state) => ({
      from: state.userRange.from,
      to: state.userRange.to,
      isLoading: state.userRange.isLoading,
    })),
  );
}

/**
 * Hook to get cursor timestamp.
 */
export function useCursor() {
  return useStreamScreenStore((state) => state.cursor.ts);
}

/**
 * Hook to get focus state.
 */
export function useFocus() {
  return useStreamScreenStore((state) => state.focus);
}

/**
 * Hook to get memoized focus range (from, to).
 * Uses useShallow so the returned object is stable when from/to are unchanged.
 */
export function useFocusRange() {
  return useStreamScreenStore(
    useShallow((state) => ({
      from: state.focus.from,
      to: state.focus.to,
    })),
  );
}

/**
 * Hook to get setCursor action.
 */
export function useSetCursor() {
  return useStreamScreenStore((state) => state.setCursor);
}

/**
 * Hook to get clearCursor action.
 */
export function useClearCursor() {
  return useStreamScreenStore((state) => state.clearCursor);
}

/**
 * Hook to get setFocus action.
 */
export function useSetFocus() {
  return useStreamScreenStore((state) => state.setFocus);
}

/**
 * Hook to get setUserRange action.
 */
export function useSetUserRange() {
  return useStreamScreenStore((state) => state.setUserRange);
}

/**
 * Hook to get setSegmentsRange action.
 */
export function useSetSegmentsRange() {
  return useStreamScreenStore((state) => state.setSegmentsRange);
}

/**
 * Hook to get setFocusCenteredAtTs action.
 * Centers focus on a target timestamp while preserving duration.
 * When the target timestamp is too close to stream boundaries, the focus is extended
 * in the opposite direction to maintain the original duration.
 */
export function useSetFocusCenteredAtTs() {
  return useStreamScreenStore((state) => state.setFocusCenteredAtTs);
}

/**
 * Hook to get setFocusAtTs action.
 * Shifts focus so it starts at the target timestamp while preserving duration.
 * When the target timestamp is too close to the end of the stream, the focus is extended
 * backwards to maintain the original duration.
 */
export function useSetFocusAtTs() {
  return useStreamScreenStore((state) => state.setFocusAtTs);
}
